/**
 * ProductLadder — the "buy me next" progression shown on the customer
 * homepage. Renders the 7-product catalog in three tiers:
 *
 *   Tier 1 — 4 individual tools (Cost Calculator, RV vs RV, Deal Analyzer,
 *            Negotiation Coach). Small tiles, real product art.
 *   Tier 2 — Complete Buyer Toolkit ($59.99) — MOST POPULAR. Big feature
 *            card, gradient border, savings pill.
 *   Tier 3 — VIP Buying Plan ($149) and VIP Concierge ($399). Two hero
 *            cards side-by-side with feature bullet lists.
 *
 * Every card links to a `/tool-info/{slug}` learn-more page (existing
 * route) where the actual Stripe checkout button lives, so this component
 * stays purely presentational.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Star, Crown } from 'lucide-react';
import { CONSUMER_TOOLS, CONSUMER_BUNDLE, CONSUMER_VIP } from '../data/products';

const ACCENT_RING = {
  sky:      'from-sky-500/20 to-sky-500/0 border-sky-400/30 hover:border-sky-400/60',
  emerald:  'from-emerald-500/20 to-emerald-500/0 border-emerald-400/30 hover:border-emerald-400/60',
  indigo:   'from-indigo-500/20 to-indigo-500/0 border-indigo-400/30 hover:border-indigo-400/60',
  rose:     'from-rose-500/20 to-rose-500/0 border-rose-400/30 hover:border-rose-400/60',
  amber:    'from-amber-500/20 to-amber-500/0 border-amber-400/50 hover:border-amber-400/80',
  violet:   'from-violet-500/20 to-violet-500/0 border-violet-400/30 hover:border-violet-400/60',
  fuchsia:  'from-fuchsia-500/20 to-fuchsia-500/0 border-fuchsia-400/30 hover:border-fuchsia-400/60',
};

// -------- small individual-tool card --------
const ToolCard = ({ product }) => (
  <Link
    to={product.href}
    data-testid={`tool-card-${product.id}`}
    className={`group relative rounded-2xl border bg-gradient-to-br ${ACCENT_RING[product.accent]} bg-white/[0.02] backdrop-blur-sm overflow-hidden transition-all duration-300 hover:-translate-y-1`}
  >
    {product.image ? (
      <div className="aspect-square w-full overflow-hidden bg-[#05070d]">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
        />
      </div>
    ) : (
      <div className="aspect-square w-full bg-gradient-to-br from-white/5 to-white/0 flex items-center justify-center text-white/40 text-xs font-bold uppercase tracking-widest">
        {product.name}
      </div>
    )}
    <div className="p-4 sm:p-5">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <h3 className="text-base sm:text-lg font-bold text-white leading-tight">{product.name}</h3>
        <span className="shrink-0 text-sm font-black text-white/90">{product.priceLabel}</span>
      </div>
      <p className="text-xs sm:text-sm text-slate-400 leading-relaxed mb-3">{product.tagline}</p>
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 group-hover:text-white transition-colors">
        Learn more <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
      </span>
    </div>
  </Link>
);

// -------- headline "Most Popular" bundle card --------
const BundleCard = ({ product }) => (
  <Link
    to={product.href}
    data-testid={`bundle-card-${product.id}`}
    className="group relative block rounded-3xl overflow-hidden ring-2 ring-amber-400/50 shadow-[0_30px_90px_-20px_rgba(245,158,11,0.35)] transition-shadow hover:shadow-[0_40px_100px_-10px_rgba(245,158,11,0.55)]"
  >
    {/* Animated glow */}
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-amber-500/20 via-amber-400/10 to-transparent" />
    <div className="absolute top-4 right-4 z-10 flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400 text-[#05070d] text-[10px] font-black tracking-widest">
      <Star className="w-3 h-3 fill-current" /> MOST POPULAR
    </div>
    <div className="relative flex flex-col md:flex-row bg-[#05070d]">
      {product.image ? (
        <div className="md:w-1/2 aspect-square md:aspect-auto overflow-hidden bg-[#05070d]">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.02]"
          />
        </div>
      ) : (
        <div className="md:w-1/2 aspect-square md:aspect-auto bg-gradient-to-br from-amber-500/20 to-transparent" />
      )}
      <div className="md:w-1/2 p-6 sm:p-8 md:p-10 flex flex-col justify-center">
        <div className="text-[10px] font-bold tracking-[0.25em] text-amber-300 mb-2">
          THE COMPLETE BUYING SYSTEM
        </div>
        <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-white leading-tight mb-3">
          {product.name}
        </h3>
        <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-5">
          {product.description}
        </p>
        {product.includes && (
          <ul className="mb-5 grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-3">
            {product.includes.map((inc) => (
              <li key={inc} className="flex items-start gap-2 text-sm text-slate-200">
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-amber-300" />
                <span className="capitalize">{inc.replace(/_/g, ' ')}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-baseline gap-3 flex-wrap mb-5">
          <span className="text-3xl sm:text-4xl font-black text-white">{product.priceLabel}</span>
          {product.savingsLabel && (
            <span className="px-2 py-1 rounded-full bg-emerald-400/15 border border-emerald-400/30 text-emerald-300 text-xs font-black tracking-wider">
              {product.savingsLabel}
            </span>
          )}
          <span className="text-xs text-slate-400 font-medium">· One-time purchase</span>
        </div>
        <span
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-amber-400 text-[#05070d] font-black text-sm shadow-[0_10px_40px_-10px_rgba(245,158,11,0.6)] group-hover:shadow-[0_15px_60px_-10px_rgba(245,158,11,0.85)] transition-shadow"
          data-testid={`bundle-cta-${product.id}`}
        >
          Get the Toolkit <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </div>
  </Link>
);

// -------- VIP tier card (Buying Plan or Concierge) --------
const VipCard = ({ product }) => (
  <Link
    to={product.href}
    data-testid={`vip-card-${product.id}`}
    className={`group relative rounded-3xl border bg-gradient-to-br ${ACCENT_RING[product.accent]} bg-white/[0.02] backdrop-blur-sm overflow-hidden transition-all duration-300 hover:-translate-y-1`}
  >
    <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-20 blur-3xl pointer-events-none"
         style={{ background: product.accent === 'violet' ? '#8b5cf6' : '#e879f9' }} />

    {/* Hero art (bright gold/blue product tile) — object-contain so the full
        VIP illustration (title + compass/crown motif) is always visible; the
        source PNGs already have their own dark backdrop so no crop is needed. */}
    {product.image ? (
      <div className="relative aspect-square w-full overflow-hidden bg-[#05070d]">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
        />
      </div>
    ) : null}

    <div className="relative p-6 sm:p-8">
      <div className="flex items-center gap-2 mb-3">
        <Crown className={`w-4 h-4 ${product.accent === 'violet' ? 'text-violet-300' : 'text-fuchsia-300'}`} />
        <span className={`text-[10px] font-bold tracking-[0.25em] ${product.accent === 'violet' ? 'text-violet-300' : 'text-fuchsia-300'}`}>
          VIP TIER
        </span>
      </div>
      <h3 className="text-xl sm:text-2xl font-black text-white leading-tight mb-2">
        {product.name}
      </h3>
      <p className="text-sm text-slate-300 leading-relaxed mb-4">
        {product.description}
      </p>
      <ul className="space-y-1.5 mb-5">
        {(product.features || []).map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-200">
            <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${product.accent === 'violet' ? 'text-violet-300' : 'text-fuchsia-300'}`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-baseline gap-2 mb-4">
        <span className="text-3xl font-black text-white">{product.priceLabel}</span>
        <span className="text-xs text-slate-400 font-medium">· One-time</span>
      </div>
      <span className="inline-flex items-center gap-2 text-sm font-bold text-white group-hover:text-white transition-colors">
        Learn more <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      </span>
    </div>
  </Link>
);

const ProductLadder = () => (
  <section
    id="product-ladder"
    className="relative bg-[#05070d] text-white py-20 md:py-24 overflow-hidden border-t border-white/5"
    data-testid="product-ladder"
  >
    <div className="pointer-events-none absolute inset-0 opacity-25">
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-amber-500 blur-[140px]" />
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-emerald-500 blur-[140px]" />
    </div>

    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10 md:mb-14">
        <p className="text-xs font-bold tracking-[0.25em] text-emerald-400 mb-3">DECISION TOOLS</p>
        <h2 className="text-3xl sm:text-5xl font-black tracking-tight">The AI-Powered RV Buying System.</h2>
        <p className="text-slate-400 mt-3 max-w-2xl mx-auto text-sm sm:text-base">
          The free RV Match quiz stays free forever. When you're ready to go deeper, these are the
          tools that walk you from "curious" to "keys in hand" — all one-time purchases, no subscriptions.
        </p>
      </div>

      {/* Tier 1 — four individual tools */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-8 md:mb-10">
        {CONSUMER_TOOLS.map((p) => (
          <ToolCard key={p.id} product={p} />
        ))}
      </div>

      {/* Tier 2 — headline bundle */}
      {CONSUMER_BUNDLE ? (
        <div className="mb-8 md:mb-10">
          <BundleCard product={CONSUMER_BUNDLE} />
        </div>
      ) : null}

      {/* Tier 3 — VIP */}
      {CONSUMER_VIP.length > 0 ? (
        <>
          <div className="text-center mb-6 md:mb-8">
            <p className="text-xs font-bold tracking-[0.25em] text-fuchsia-300 mb-2">FOR WHEN YOU WANT A HUMAN IN YOUR CORNER</p>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tight">Personal RV-buying help.</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            {CONSUMER_VIP.map((p) => (
              <VipCard key={p.id} product={p} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  </section>
);

export default ProductLadder;
